import { TableIds } from "../../../common/table-identifier-utils"
import {
    checkboxColDefCreator,
    companyStartStopTypeColDefCreator,
    dateColDefCreator,
    datetimeColDefCreator,
    editableTimeCardColDefCreator,
    employeeColDefCreator,
    lengthLimitedColDefCreator,
    stringColDefCreator,
    timekeepingStatusColDefCreator,
} from "./standard-col-def-creators"
import {
    alertCellClassRule,
    getAvailableStatusValues,
    isGroupRow,
    timekeepingDataIsEditable,
    timekeepingRowIsEditable,
} from "../../../common/ag-grid-utils"
import GroupRowFormattingDefaultCellRenderer from "../cell-renderers/GroupRowFormattingDefaultCellRenderer"
import { getSelectorActionButtons } from "./action-button-settings"
import { isNumber } from "../../../common/validators"
import { getFlagEnabled } from "../../../getFlagValue"
import {
    doesShiftEndingNextDayExist,
    getShiftNodeForEmployee,
    findShiftRowData,
    getObjectId,
    lockedPeriodCellClassRule,
} from "../../../common/ag-grid-ts-utils"
import { addDays, addMinutes, differenceInMinutes, parseISO } from "date-fns"
import { simpleTimeValueFormatter } from "../../../common/ag-grid-value-formatters"
import { compareGroupColumns } from "../../../common/ag-grid-comparators"
import {
    timekeepingCommentTooltip,
    timekeepingEntryGeneralErrorsTooltip,
} from "../../../common/ag-grid-custom-tooltip-formatters"
import { weeklyTkDummyProject } from "../../../common/constants"
import { toggleCicoVisible, togglePlaceholdersVisible } from "../../../actions"
import { convertDecimalHoursToHoursAndMinutes, getAsDate, isDateTheNextDay } from "../../../common/ts-utils"
import { colorAttentionBlue50, colorAttentionRed60, colorFunctionalGray50 } from "@rhumbix/rmbx_design_system_web"
import { cicoEnabledFromLockedColumns } from "../utils"
import {
    addEmployeeRowsTableAction,
    addRowTableAction,
    editTimecardDetailsAction,
    viewCicoTableButton,
    viewCicoForSelectedRowsButton,
} from "./common-button-defs"
import {
    CellClassParams,
    ICellEditorParams,
    RowNode,
    ValueFormatterParams,
    ValueGetterParams,
} from "ag-grid-community"
import { ExceptionsState, iCurrentUser, tFeatures } from "../../../common/types"
import { tProject } from "../../../cached-data/types"
import { tContext } from "../types"
import { tSourceData } from "../../../dashboard-data/types"
import { toggleException } from "../../../actions/exceptions"
import { activateException } from "../custom-filters/utils"
import { Dispatch } from "react"

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const getLocalWorkshiftDate = (params: ICellEditorParams) => {
    let date = new Date(params.data?.date)
    date = addMinutes(date, date.getTimezoneOffset())
    return date
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const getLocalWorkshiftStartOrStop = (params: ICellEditorParams) => {
    if (params.data?.company_start_stop_type?.is_break === false) {
        if (params.colDef.field === "/start_time") return getAsDate(params.data.shift_start_time)
        else if (params.colDef.field === "/stop_time") return getAsDate(params.data.shift_end_time)
    }
    return null
}

const allExceptionsToggle = (context: tContext) => {
    const exceptionTypes = ["workRulesException"]
    const allOn = exceptionTypes.every(
        et => (context.exceptions[et as keyof ExceptionsState] as { enabled: boolean }).enabled
    )
    const allOff = exceptionTypes.every(
        et => !(context.exceptions[et as keyof ExceptionsState] as { enabled: boolean }).enabled
    )
    const isMixed = !allOn && !allOff
    const desiredState = !!(isMixed || allOff)
    return (dispatch: Dispatch<any>) => {
        if (context.exceptions.workRulesException.enabled !== desiredState)
            dispatch(toggleException("workRulesException"))
    }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getShiftsAndBreaksTable = (featureFlags: tFeatures, currentProject: tProject, user: iCurrentUser) => {
    const userRole = user ? user.user_role : undefined
    const parentContainerId = getFlagEnabled("WA-8881-cico-meal-break-min")
        ? { parentContainerId: "weekly-tk-detail-table-modal" }
        : {}
    return {
        tableName: "Shifts and Breaks",
        id: TableIds.ShiftsAndBreaksModal,
        navId: "shifts-and-breaks",
        resources: ["ewsStartStopTimes"],
        cicoResource: "timelineEntryVersions",
        colDefs: [
            dateColDefCreator({}),
            {
                ...checkboxColDefCreator({}),
                checkboxSelection: (p: RowNode) => !isGroupRow(p),
            },
            {
                ...employeeColDefCreator({
                    cellRenderer: GroupRowFormattingDefaultCellRenderer,
                }),
                ignoreRelatedFilters: ["projectId"],
            },
            {
                ...editableTimeCardColDefCreator({
                    headerName: "Time Card",
                    required: true,
                    width: 300,
                    features: featureFlags,
                }),
                ignoreRelatedFilters: ["projectId"],
                extraFilterQueryParams: ["start_date", "foreman_id"],
                extraSearchFilters: { allow_empty: true },
                actionOptions: [getSelectorActionButtons().createNewTimecardButton],
            },
            companyStartStopTypeColDefCreator({
                required: true,
                cellRenderer: GroupRowFormattingDefaultCellRenderer,
            }),
            {
                ...datetimeColDefCreator({
                    cellEditorParams: {
                        minuteInterval: 1,
                        getOpenToDate: getLocalWorkshiftDate,
                        getMinDate: getLocalWorkshiftDate,
                        getMaxDate: (params: ICellEditorParams) => {
                            if (getFlagEnabled("WA-8799-workshift-date-fix")) {
                                const localWorkshiftDate = getLocalWorkshiftDate(params)
                                const isShift =
                                    params.data?.is_break === false ||
                                    params.data?.company_start_stop_type?.is_break === false

                                // Shifts must start on the same day as the shift date (in local time)
                                if (isShift) {
                                    return localWorkshiftDate
                                }

                                // Breaks/meals can start on the next day if the shift ends on the next day
                                const shiftData = getShiftNodeForEmployee(params.api, params.data)
                                if (shiftData?.stop_time) {
                                    const shiftEntryEndTime = parseISO(shiftData.stop_time)
                                    return isDateTheNextDay(localWorkshiftDate, shiftEntryEndTime)
                                        ? addDays(localWorkshiftDate, 1)
                                        : localWorkshiftDate
                                }

                                // Otherwise, breaks/meals must start on the same day
                                return localWorkshiftDate
                            }

                            // Remove the rest of the function below when removing flag WA-8799-workshift-date-fix
                            const localWorkshiftDate = getLocalWorkshiftDate(params)
                            const employeeId = isNumber(params.data?.employee)
                                ? params.data?.employee
                                : params.data?.employee?.id

                            if (getFlagEnabled("WA-7464-no-next-day-date-picker")) {
                                // Be more restrictive, not allowing selection of the next
                                // day unless we're sure it's a break and there's a shift
                                // for the same person which ends on the next day
                                if (
                                    // this is the line that's causing the issue fixed by WA-8799-workshift-date-fix
                                    // there's a handful of other issues that I ran into while testing that I
                                    // ended up just replacing it by the code above.
                                    // When creating the shift entry, the company_start_stop_type is left undefined,
                                    // only the top-level is_break is set.
                                    params.data?.company_start_stop_type?.is_break === true &&
                                    doesShiftEndingNextDayExist(params.api, localWorkshiftDate, employeeId)
                                ) {
                                    return addDays(localWorkshiftDate, 1)
                                } else return localWorkshiftDate
                            } else {
                                if (params.data?.is_break === false) {
                                    // Limit Shift start to the workshift date.
                                    return localWorkshiftDate
                                }
                                return addDays(localWorkshiftDate, 1)
                            }
                        },
                        getInitialValue: getLocalWorkshiftStartOrStop,
                        stopEditAfterSelectTime: true,
                    },
                    cellRenderer: GroupRowFormattingDefaultCellRenderer,
                    field: "/start_time",
                    headerName: "Start Time",
                    required: true,
                    valueFormatter: (params: ValueFormatterParams) => {
                        if (isGroupRow(params) && params.node) {
                            const shift = findShiftRowData(params.node)
                            if (!shift) return ""
                            return simpleTimeValueFormatter({
                                value: shift.start_time,
                            })
                        }
                        return simpleTimeValueFormatter(params)
                    },
                }),
                minWidth: 320,
            },
            {
                ...datetimeColDefCreator({
                    cellEditorParams: {
                        minuteInterval: 1,
                        getOpenToDate: getLocalWorkshiftDate,
                        getMinDate: getLocalWorkshiftDate,
                        getMaxDate: (params: ICellEditorParams) => {
                            if (getFlagEnabled("WA-8799-workshift-date-fix")) {
                                const localWorkshiftDate = getLocalWorkshiftDate(params)
                                const isShift =
                                    params.data?.is_break === false ||
                                    params.data?.company_start_stop_type?.is_break === false
                                // Shifts can always end on the next day
                                if (isShift) {
                                    return addDays(localWorkshiftDate, 1)
                                }

                                // Breaks/meals can end on the next day if the shift ends on the next day
                                const shiftData = getShiftNodeForEmployee(params.api, params.data)
                                if (shiftData?.stop_time) {
                                    const shiftEntryEndTime = parseISO(shiftData.stop_time)
                                    return isDateTheNextDay(localWorkshiftDate, shiftEntryEndTime)
                                        ? addDays(localWorkshiftDate, 1)
                                        : localWorkshiftDate
                                }

                                // Otherwise, breaks/meals must end on the same day
                                return localWorkshiftDate
                            }

                            // The code below didn't have the same issue as start_time, but
                            // for consistency, it was replaced as well.  Remove the code below
                            // when removing flag WA-8799-workshift-date-fix

                            const workShiftDate = getLocalWorkshiftDate(params)
                            const nextDay = addDays(workShiftDate, 1)
                            const isBreak = params.data?.company_start_stop_type?.is_break
                            const employeeId = isNumber(params.data?.employee)
                                ? params.data?.employee
                                : params.data?.employee?.id

                            if (getFlagEnabled("WA-7464-no-next-day-date-picker")) {
                                // Early out for shifts. We explicitly check for false
                                // so that undefined stuff doesn't allow for next day
                                if (isBreak === false) return nextDay

                                // Allow the next day if we can find a shift with an end
                                // date as the next day
                                const nextDayShiftEnd = doesShiftEndingNextDayExist(
                                    params.api,
                                    workShiftDate,
                                    employeeId
                                )
                                return nextDayShiftEnd ? nextDay : workShiftDate
                            }
                            return nextDay
                        },
                        getInitialValue: getLocalWorkshiftStartOrStop,
                        stopEditAfterSelectTime: true,
                    },
                    cellRenderer: GroupRowFormattingDefaultCellRenderer,
                    field: "/stop_time",
                    headerName: "Stop Time",
                    required: true,
                    valueFormatter: (params: ValueFormatterParams) => {
                        if (isGroupRow(params) && params.node) {
                            const shift = findShiftRowData(params.node)
                            if (!shift) return ""
                            return simpleTimeValueFormatter({
                                value: shift.stop_time,
                            })
                        }
                        return simpleTimeValueFormatter(params)
                    },
                }),
                minWidth: 320,
            },
            {
                ...stringColDefCreator({
                    cellRenderer: GroupRowFormattingDefaultCellRenderer,
                    editable: false,
                    headerName: "Duration",
                    valueGetter: (params: ValueGetterParams) => {
                        let { data } = params
                        if (isGroupRow(params) && params.node) {
                            const shift = findShiftRowData(params.node)
                            data = shift
                        }
                        if (!data) return

                        const { is_break, start_time, stop_time } = data
                        if (!start_time || !stop_time) {
                            return ""
                        }

                        const durationInMinutes = differenceInMinutes(new Date(stop_time), new Date(start_time))

                        if (is_break) {
                            return `${durationInMinutes} minutes`
                        }

                        const shiftHours = Math.floor(durationInMinutes / 60)
                        const shiftMinutes = durationInMinutes % 60
                        let shiftDurationText = ""
                        if (shiftHours) {
                            shiftDurationText += `${shiftHours} hours`
                        }
                        if (shiftMinutes) {
                            shiftDurationText += `${shiftHours ? ", " : ""}${shiftMinutes} minutes`
                        }

                        return shiftDurationText
                    },
                }),
            },
            // This value gets computed on the fly
            ...(featureFlags.cico
                ? [
                      {
                          headerName: "CI/CO",
                          field: "/cico_diff",
                          minWidth: 100,
                          editable: false,
                          cellStyle: (params: CellClassParams) => {
                              if (params.value === "--") return { color: colorFunctionalGray50 }
                              const val = parseFloat(params.value)
                              if (val > 0) return { color: colorAttentionBlue50 }
                              else if (val < 0) return { color: colorAttentionRed60 }
                          },
                          valueGetter: (params: ValueGetterParams) => {
                              const cicoEnabled = cicoEnabledFromLockedColumns(params.context)
                              const { referenceableData } = params.context
                              let timecardCicoEnabled = true
                              if (params.data.work_shift_id) {
                                  const workShift =
                                      referenceableData.workShifts[getObjectId(params.data.work_shift_id)]

                                  timecardCicoEnabled = workShift?.projects?.some(
                                      (pId: number) => referenceableData.projects[pId]?.cico_settings?.enabled
                                  )
                              }
                              if (getFlagEnabled("RN-2134-round-cico-totals")) {
                                  return cicoEnabled && timecardCicoEnabled
                                      ? convertDecimalHoursToHoursAndMinutes(params.data.cico_diff)
                                      : "--"
                              }
                              return cicoEnabled && timecardCicoEnabled ? params.data.cico_diff : "--"
                          },
                      },
                  ]
                : []),
            ...(getFlagEnabled("WA-8911-shift-break-comment") && featureFlags.timekeeping_comments
                ? [
                      {
                          ...lengthLimitedColDefCreator({
                              headerName: "Comment",
                              field: "/comment",
                              maxLength: user.company_options.tkcomment_max_length,
                          }),
                          customTooltip: timekeepingCommentTooltip,
                      },
                  ]
                : []),
            {
                ...timekeepingStatusColDefCreator({
                    enum: getAvailableStatusValues(userRole),
                }),
                cellRenderer: GroupRowFormattingDefaultCellRenderer,
                aggFunc: "statusColumnAggFunc",
            },
        ],
        gridSettings: {
            autoGroupColumnDef: {
                headerName: "Type",
                suppressMenu: true,
                sort: "asc",
                unSortIcon: true,
                cellRendererParams: {
                    suppressCount: true,
                },
                comparator: compareGroupColumns,
                checkboxSelection: (p: RowNode) => isGroupRow(p),
                groupSelectsChildren: false,
                minWidth: 250,
            },
            defaultColDef: {
                cellClassRules: {
                    "alert-cell": alertCellClassRule,
                    "period-locked": lockedPeriodCellClassRule,
                    readonly: (params: CellClassParams) => !timekeepingDataIsEditable(params).canEdit,
                },
                editable: (params: CellClassParams) => timekeepingDataIsEditable(params).canEdit,
                filter: false,
                customTooltip: (params: CellClassParams) => timekeepingEntryGeneralErrorsTooltip(params, true),
                warningsAndErrorsTooltip: getFlagEnabled("WA-8985-work-rules"),
                ...parentContainerId,
            },
            enableRangeSelection: true,
            rowSelection: "multiple",
            suppressRowClickSelection: true,
            suppressMultiRangeSelection: true,
            isRowSelectable: (rowNode: RowNode) => timekeepingRowIsEditable(rowNode),
        },
        otherSettings: {
            pasteRangeEnabled: true,
            enableClipboard: true,
            filterByLockedColumns: true,
            hideLockedColumns: true,
            highlightErrorRows: true,
            isInModalTab: true,
            sizeColumnsToFitSize: 915,
            rowLevelErrorDisplay: true,
            suppressExportButton: true,
            exceptionFilters: getFlagEnabled("WA-8985-work-rules") ? ["workRulesException"] : [],
            hiddenColumnDefaults: {
                project: {
                    value: weeklyTkDummyProject,
                },
            },
            groupBy: [
                {
                    cols: [],
                    label: "None",
                },
                {
                    cols: ["/employee"],
                    label: "Employee",
                },
            ],
            buttons: {
                cell: [],
                row: [
                    {
                        label: "Delete",
                        icon: "delete",
                        action: "deleteSelectedRows",
                        args: {
                            local_state_update: true,
                        },
                    },
                    editTimecardDetailsAction,
                    ...(featureFlags.cico && getFlagEnabled("WA-8957-cico-shifts-breaks")
                        ? [viewCicoForSelectedRowsButton]
                        : []),
                ],
                groupRow: [
                    {
                        label: "Add Row",
                        icon: "add",
                        action: "addNewRowToGroup",
                    },
                ],
                table: [
                    {
                        label: "Group By",
                        action: "groupBy",
                        icon: "group",
                    },
                    addRowTableAction(featureFlags),
                    addEmployeeRowsTableAction,
                    editTimecardDetailsAction,
                    ...(featureFlags.cico
                        ? [
                              {
                                  label: "Show/Hide ▾",
                                  icon: "view",
                                  args: {
                                      extraArgs: {
                                          options: [
                                              {
                                                  label: "Placeholders",
                                                  action: togglePlaceholdersVisible,
                                                  // The state parameter we look at to see if this should be
                                                  // checked on
                                                  activeStateParams: ["placeholdersVisible"],
                                              },
                                              {
                                                  label: "CI/CO Data",
                                                  action: toggleCicoVisible,
                                                  // The state parameter we look at to see if this should be
                                                  // checked on
                                                  activeStateParams: ["cicoVisible"],
                                                  disabled: (ctx: tContext) => !cicoEnabledFromLockedColumns(ctx),
                                              },
                                          ],
                                      },
                                  },
                                  checklist: true,
                              },
                              ...(getFlagEnabled("WA-8957-cico-shifts-breaks")
                                  ? [
                                        viewCicoTableButton,
                                        /* eslint-disable @typescript-eslint/explicit-module-boundary-types */
                                        {
                                            label: "Import From CI/CO",
                                            icon: "import",
                                            action: "importShiftsAndBreaksFromCico",
                                            disabled: (_: tContext, sourceData: tSourceData) => {
                                                return !!sourceData.ewsStartStopTimes?.filter(r => !r.is_cico_row)
                                                    .length
                                            },
                                            tooltip: (_: tContext, sourceData: tSourceData) => {
                                                return sourceData.ewsStartStopTimes?.filter(r => !r.is_cico_row)
                                                    .length
                                                    ? "You must remove all existing Shifts and Breaks \
                                                    before importing from CI/CO"
                                                    : "Import Shifts and Breaks from CI/CO data"
                                            },
                                        },
                                        /* eslint-enable @typescript-eslint/explicit-module-boundary-types */
                                    ]
                                  : []),
                              ...(getFlagEnabled("WA-8985-work-rules")
                                  ? [
                                        {
                                            label: "Exceptions ▾",
                                            icon: "exception",
                                            args: {
                                                extraArgs: {
                                                    options: [
                                                        {
                                                            label: "Exceptions",
                                                            action: allExceptionsToggle,
                                                            exceptionParams: ["workRulesException"],
                                                            hasGear: false,
                                                        },
                                                        {
                                                            label: "Work Rules",
                                                            action: activateException(() =>
                                                                toggleException("workRulesException")
                                                            ),
                                                            exceptionParams: ["workRulesException"],
                                                            hasGear: true,
                                                            gearModalType: "workRulesException",
                                                            isChild: true,
                                                        },
                                                    ],
                                                    filterToggle: true,
                                                    exceptionList: true,
                                                },
                                            },
                                            checklist: true,
                                        },
                                    ]
                                  : []),
                          ]
                        : []),
                ],
            },
        },
    }
}
